import {
  RequestParameters,
  RequestTransformFunction,
  ResourceType,
} from 'mapbox-gl';
import { useCallback } from 'react';
import { getAuthenticationHeaders } from 'api';
import axios, { AxiosResponse } from 'axios';
import { isProductionEnvironment } from 'shared/envUtils';
import { ENV } from 'shared/ENV';
import { SESSION_STORAGE_ACCESSKEY_KEY } from 'state/localStorage';

const DEV_ONLY_API_BASE_URL = 'http://localhost:8000/api/v1/';

const isBackendUrl = (url: string): boolean => {
  if (url.startsWith(ENV.VITE_BASE_API_URL)) {
    return true;
  }

  // In local dev, the backend will report it's running on localhost:8000, but the VITE env in the frontend
  // may be a network IP address. So we need to check for both.
  if (!isProductionEnvironment() && url.startsWith(DEV_ONLY_API_BASE_URL)) {
    return true;
  }
  return false;
};

export const useAuthenticatedTileRequestTransformer =
  (): RequestTransformFunction => {
    const addAuthHeadersToRequest = useCallback(
      (
        url: string,
        _resourceTypeEnum: ResourceType | unknown,
      ): RequestParameters => {
        if (!isBackendUrl(url)) {
          return { url };
        }

        // Mapbox's transformRequest() cannot be async, so we can only get our auth token from sessionStorage
        const token = sessionStorage.getItem(SESSION_STORAGE_ACCESSKEY_KEY);

        return {
          url,
          headers: {
            ...(token && { Authorization: `Token ${token}` }),
          },
        };
      },
      [],
    );

    return addAuthHeadersToRequest;
  };

export const AuthenticatedTileAPI = {
  /**
   * Performs a HEAD request to the given URL. If the URL is a backend URL, adds auth headers.
   */
  head: async (url: string): Promise<AxiosResponse> => {
    const headers = await getAuthenticationHeaders();
    if (isBackendUrl(url)) {
      return axios.head(url, {
        headers,
      });
    }
    return axios.head(url);
  },
  /**
   * Performs a GET request to the given URL. If the URL is a backend URL, it adds auth headers.
   */
  get: async (url: string): Promise<AxiosResponse> => {
    const headers = await getAuthenticationHeaders();
    if (isBackendUrl(url)) {
      return axios.get(url, {
        headers,
      });
    }
    return axios.get(url);
  },
};
